import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getApproveAllConfirmation, consumeApproveAll } from '../api';

// Unauthenticated confirmation page for the weekly digest's "Approve All"
// button (issue #35). The page is deliberately NOT behind ProtectedRoute: the
// whole point is approving from the email without an app login. Auth is the
// opaque token in the URL.
//
// Two-step + prefetch-safe: on mount we GET (read-only) to confirm the token
// and show what would be approved; the destructive bulk approval only fires on
// the explicit POST when the user clicks the button.
function ApproveAll() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [phase, setPhase] = useState('loading'); // loading|confirm|done|error
  const [confirmation, setConfirmation] = useState(null);
  const [result, setResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setPhase('error');
      setErrorMessage('This approval link is missing its token.');
      return;
    }
    getApproveAllConfirmation(token)
      .then((resp) => {
        const data = resp.data;
        if (!data.valid) {
          setPhase('error');
          setErrorMessage(messageForStatus(data.status));
          return;
        }
        setConfirmation(data);
        setPhase('confirm');
      })
      .catch(() => {
        setPhase('error');
        setErrorMessage('We could not load this approval link.');
      });
  }, [token]);

  function messageForStatus(status) {
    if (status === 'expired') {
      return "This link has expired. Open ERBI or wait for Monday's email.";
    }
    if (status === 'consumed') {
      return 'This link was already used.';
    }
    return 'This approval link is invalid.';
  }

  function handleApprove() {
    setSubmitting(true);
    consumeApproveAll(token)
      .then((resp) => {
        setResult(resp.data);
        setPhase('done');
      })
      .catch((err) => {
        const detail = err?.response?.data?.detail;
        setErrorMessage(
          (detail && detail.message) || 'This approval link could not be used.'
        );
        setPhase('error');
      })
      .finally(() => setSubmitting(false));
  }

  const pendingInvoices = (confirmation?.invoices || []).filter((i) => i.pending);

  return (
    <div style={styles.wrap}>
      <div style={styles.card}>
        <h2 style={styles.heading}>Approve All</h2>

        {phase === 'loading' && <p>Checking your link…</p>}

        {phase === 'error' && (
          <p style={styles.error}>{errorMessage}</p>
        )}

        {phase === 'confirm' && (
          <>
            <p style={{ marginTop: 0 }}>
              {confirmation.approver_name
                ? `Hi ${confirmation.approver_name},`
                : 'Hello,'}{' '}
              you are about to approve{' '}
              <strong>{pendingInvoices.length}</strong> invoice
              {pendingInvoices.length === 1 ? '' : 's'}.
            </p>
            <table style={styles.table}>
              <thead>
                <tr style={styles.theadRow}>
                  <th style={styles.th}>Vendor</th>
                  <th style={styles.th}>Invoice</th>
                  <th style={styles.th}>Amount</th>
                  <th style={styles.th}>Date</th>
                </tr>
              </thead>
              <tbody>
                {confirmation.invoices.map((inv) => (
                  <tr key={inv.request_id} style={inv.pending ? null : styles.staleRow}>
                    <td style={styles.td}>{inv.vendor_name || 'Unknown vendor'}</td>
                    <td style={styles.td}>{inv.invoice_number || '—'}</td>
                    <td style={styles.td}>
                      {inv.amount ? `${inv.amount} ${inv.currency || ''}` : '—'}
                    </td>
                    <td style={styles.td}>{inv.invoice_date || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button
              onClick={handleApprove}
              disabled={submitting}
              style={styles.button}
            >
              {submitting ? 'Approving…' : `Approve ${pendingInvoices.length} invoice${pendingInvoices.length === 1 ? '' : 's'}`}
            </button>
          </>
        )}

        {phase === 'done' && result && (
          <p style={styles.success}>
            Approved {result.approved} invoice{result.approved === 1 ? '' : 's'}
            {result.skipped > 0
              ? ` (${result.skipped} already handled and skipped).`
              : '.'}
          </p>
        )}
      </div>
    </div>
  );
}

const styles = {
  wrap: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    background: '#f6f7f9',
    padding: '48px 16px',
  },
  card: {
    background: '#fff',
    borderRadius: 8,
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
    padding: 28,
    maxWidth: 560,
    width: '100%',
    fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif",
    color: '#1a1a1a',
  },
  heading: { marginTop: 0 },
  table: { borderCollapse: 'collapse', width: '100%', margin: '16px 0', fontSize: 14 },
  theadRow: { textAlign: 'left', color: '#666', fontSize: 12 },
  th: { padding: '0 12px 6px 0' },
  td: { padding: '4px 12px 4px 0' },
  staleRow: { color: '#aaa', textDecoration: 'line-through' },
  button: {
    display: 'inline-block',
    background: '#2e7d32',
    color: '#fff',
    padding: '10px 18px',
    border: 'none',
    borderRadius: 5,
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: 14,
  },
  error: { color: '#c62828' },
  success: { color: '#2e7d32' },
};

export default ApproveAll;
